import { Injectable } from '@angular/core';
import {
  Observable,
  of,
  delay,
  map,
  catchError,
  mergeMap,
  from,
  switchMap,
} from 'rxjs';
import { environment } from '@environments/environment';
import { BpmnItem, MOCK_BPMN_DATA } from '../bpmn.config';
import { ProcessDefineControllerService } from '@app/api/order-service/api/processDefineController.service';
import { ProcessDefinePageDTO } from '@app/api/order-service/model/processDefinePageDTO';
import { PageRequestDTO } from '@app/api/order-service/model/pageRequestDTO';
import { ProcessDefinePageVO } from '@app/api/order-service/model/processDefinePageVO';
import { ProcessDefine } from '@app/api/order-service/model/processDefine';
import { ProcessDefineDetailsVO } from '@app/api/order-service/model/processDefineDetailsVO';
import { ProcessDefineDTO } from '@app/api/order-service/model/processDefineDTO';

export interface BpmnSearchParams {
  processName?: string;
  page: number;
  size: number;
}

export interface BpmnResponse {
  data: BpmnItem[];
  totalRecords: number;
  page: number;
  size: number;
}

export interface BpmnProcessDetails {
  process: BpmnItem;
  xmlContent: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class BpmnService {
  private readonly mockData = [...MOCK_BPMN_DATA];

  constructor(
    private readonly processDefineController: ProcessDefineControllerService,
  ) {
    // CAMUNDA-MIGRATION: process-definition APIs moved order-service -> workflow-service
    // (same paths + DTOs). Re-point the generated client at the workflow-service BFF route.
    this.processDefineController.configuration.basePath =
      environment.workflowServiceBasePath;
  }

  /**
   * Get BPMN processes with pagination and search
   */
  getBpmnProcesses(params: BpmnSearchParams): Observable<BpmnResponse> {
    // Prepare API request parameters
    const processDefine: ProcessDefinePageDTO = params.processName
      ? { processName: params.processName }
      : {};

    const pageRequest: PageRequestDTO = {
      page: params.page - 1, // Convert to 0-based for API
      size: params.size,
      sortBy: 'creationDate',
      ascending: false,
    };

    // Make real API call
    return this.processDefineController.list4(processDefine, pageRequest).pipe(
      mergeMap(
        (response) => from(this.mapApiResponseToBpmnResponse(response)), // convert Promise → Observable
      ),
      catchError((error) => {
        console.error('Error fetching BPMN processes:', error);
        return of(this.filterAndPaginateData(params));
      }),
    );
  }

  /**
   * Deploy a BPMN process
   */
  deployProcess(id: string): Observable<boolean> {
    const processId = Number.parseInt(id, 10);
    return this.processDefineController
      .deployProcess(processId)
      .pipe(map(() => true));
  }

  /**
   * Delete a BPMN process
   */
  deleteProcess(id: string): Observable<boolean> {
    const processId = Number.parseInt(id, 10);
    const processDefine: ProcessDefine = { id: processId };

    return this.processDefineController.remove4(processDefine).pipe(
      map((response) => {
        return true;
      }),
      catchError((error) => {
        console.error('Error deleting process:', error);
        return of(false);
      }),
    );
  }

  /**
   * Map API response to BpmnResponse format
   */
  private async mapApiResponseToBpmnResponse(
    apiResponse: any,
  ): Promise<BpmnResponse> {
    let json = apiResponse;

    // Convert Blob → JSON
    if (apiResponse instanceof Blob) {
      const text = await apiResponse.text();
      json = JSON.parse(text);
    }

    const pageContent = json.pageContent || [];
    const mappedData: BpmnItem[] = pageContent.map(
      (item: ProcessDefinePageVO) => this.mapProcessDefineToBpmnItem(item),
    );

    return {
      data: mappedData,
      totalRecords: json.totalElements || 0,
      page: (json.number || 0) + 1, // Convert to 1-based
      size: json.size || 20,
    };
  }

  /**
   * Map ProcessDefinePageVO to BpmnItem
   */
  private mapProcessDefineToBpmnItem(item: ProcessDefinePageVO): BpmnItem {
    return {
      id: item.id?.toString() || '',
      definitionId: item.processDefineId || '',
      deploymentId: item.status === 1 ? item.processDefineId || '' : '',
      name: item.processName || '',
      fileName: item.processFileName || '',
      version: item.objectVersionNumber || 1,
      status: item.status || 0,
      createdAt: item.creationDate || new Date().toISOString(),
      updatedAt: item.lastUpdateDate || new Date().toISOString(),
      createdBy: item.createdBy || '',
      updatedBy: item.lastUpdatedBy || '',
    };
  }

  /**
   * Map ProcessDefineDetailsVO to BpmnItem
   */
  private mapProcessDefineDetailsToBpmnItem(
    item: ProcessDefineDetailsVO,
  ): BpmnItem {
    return {
      id: item.id?.toString() || '',
      definitionId: item.processDefineId || '',
      deploymentId: item.status === 1 ? item.processDefineId || '' : '',
      name: item.processName || '',
      fileName: item.processFileName || '',
      version: 1, // Default version since it's not in details response
      status: item.status || 0,
      createdAt: new Date().toISOString(), // Use current date as fallback since not available in details
      updatedAt: new Date().toISOString(), // Use current date as fallback since not available in details
      createdBy: '', // Not available in details response
      updatedBy: '', // Not available in details response
    };
  }

  /**
   * Get a single BPMN process by ID
   */
  getBpmnProcess(id: string): Observable<BpmnItem | null> {
    const processId = Number.parseInt(id, 10);

    return this.processDefineController.detail7(processId).pipe(
      map((response: ProcessDefineDetailsVO) =>
        this.mapProcessDefineDetailsToBpmnItem(response),
      ),
      catchError((error) => {
        console.error('Error fetching process details:', error);
        // Fallback to mock data
        const process = this.mockData.find((p) => p.id === id);
        return of(process || null);
      }),
    );
  }

  /**
   * Get BPMN process details with XML content in a single API call
   */
  getBpmnProcessWithXml(id: string): Observable<BpmnProcessDetails | null> {
    const processId = Number.parseInt(id, 10);

    return this.processDefineController.detail7(processId).pipe(
      switchMap(async (response: any) => {
        // If API returns Blob, convert Blob → JSON
        if (response instanceof Blob) {
          const text = await response.text();
          response = JSON.parse(text);
        }

        const process = this.mapProcessDefineDetailsToBpmnItem(response);
        let xmlContent = response.xmlString || null;

        if (xmlContent && process.name && process.definitionId) {
          xmlContent = this.updateBpmnXmlWithProcessInfo(
            xmlContent,
            process.name,
            process.definitionId,
          );
        } else if (!xmlContent && process.name && process.definitionId) {
          xmlContent = this.generateSampleBpmnXml(
            process.name,
            process.definitionId,
          );
        }

        return {
          process,
          xmlContent,
        } as BpmnProcessDetails;
      }),
      catchError((error) => {
        console.error('Error fetching process details with XML:', error);

        const mockProcess = this.mockData.find((p) => p.id === id);
        if (mockProcess) {
          const sampleXml = this.generateSampleBpmnXml(
            mockProcess.name,
            mockProcess.definitionId,
          );
          return of({
            process: mockProcess,
            xmlContent: sampleXml,
          });
        }
        return of(null);
      }),
    );
  }

  /**
   * Get BPMN XML content by process ID
   */
  getBpmnXmlContent(id: string): Observable<string | null> {
    const processId = Number.parseInt(id, 10);

    return this.processDefineController.detail7(processId).pipe(
      map((response: ProcessDefineDetailsVO) => {
        // Return the xmlString from the API response
        if (response.xmlString) {
          return response.xmlString;
        }

        // Fallback to generating sample XML if no xmlString is provided
        const processName = response.processName || 'Unknown Process';
        const processDefineId = response.processDefineId || 'Process_Unknown';
        return this.generateSampleBpmnXml(processName, processDefineId);
      }),
      catchError((error) => {
        console.error('Error fetching BPMN XML content:', error);
        // Fallback to mock data
        const process = this.mockData.find((p) => p.id === id);
        if (process) {
          const sampleXml = this.generateSampleBpmnXml(
            process.name,
            process.definitionId,
          );
          return of(sampleXml);
        }
        return of(null);
      }),
    );
  }

  /**
   * Generate sample BPMN XML for demonstration
   * In a real implementation, this would come from the backend
   */
  private generateSampleBpmnXml(
    processName: string,
    definitionId: string,
  ): string {
    return `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL" 
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" 
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" 
                  xmlns:di="http://www.omg.org/spec/DD/20100524/DI" 
                  id="Definitions_${definitionId}" 
                  targetNamespace="http://bpmn.io/schema/bpmn" 
                  exporter="bpmn-js" 
                  exporterVersion="8.7.1">
  <bpmn:process id="${definitionId}" name="${processName}" isExecutable="true">
    <bpmn:startEvent id="StartEvent_1" name="Start">
      <bpmn:outgoing>Flow_1</bpmn:outgoing>
    </bpmn:startEvent>
    <bpmn:task id="Task_1" name="Process Task">
      <bpmn:incoming>Flow_1</bpmn:incoming>
      <bpmn:outgoing>Flow_2</bpmn:outgoing>
    </bpmn:task>
    <bpmn:endEvent id="EndEvent_1" name="End">
      <bpmn:incoming>Flow_2</bpmn:incoming>
    </bpmn:endEvent>
    <bpmn:sequenceFlow id="Flow_1" sourceRef="StartEvent_1" targetRef="Task_1" />
    <bpmn:sequenceFlow id="Flow_2" sourceRef="Task_1" targetRef="EndEvent_1" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="${definitionId}">
      <bpmndi:BPMNShape id="_BPMNShape_StartEvent_2" bpmnElement="StartEvent_1">
        <dc:Bounds x="179" y="99" width="36" height="36" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="Activity_1_di" bpmnElement="Task_1">
        <dc:Bounds x="270" y="77" width="100" height="80" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="Event_1_di" bpmnElement="EndEvent_1">
        <dc:Bounds x="432" y="99" width="36" height="36" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNEdge id="Flow_1_di" bpmnElement="Flow_1">
        <di:waypoint x="215" y="117" />
        <di:waypoint x="270" y="117" />
      </bpmndi:BPMNEdge>
      <bpmndi:BPMNEdge id="Flow_2_di" bpmnElement="Flow_2">
        <di:waypoint x="370" y="117" />
        <di:waypoint x="432" y="117" />
      </bpmndi:BPMNEdge>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;
  }

  /**
   * Update BPMN XML with process information if missing
   */
  private updateBpmnXmlWithProcessInfo(
    xmlContent: string,
    processName: string,
    definitionId: string,
  ): string {
    try {
      // Parse the XML to check if it has proper process name and ID
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

      // Find the process element
      const processElement = xmlDoc.querySelector(
        String.raw`bpmn\:process, process`,
      );

      if (processElement) {
        // Update process name and ID if they're missing or incorrect
        if (
          !processElement.getAttribute('name') ||
          processElement.getAttribute('name') !== processName
        ) {
          processElement.setAttribute('name', processName);
        }

        if (
          !processElement.getAttribute('id') ||
          processElement.getAttribute('id') !== definitionId
        ) {
          processElement.setAttribute('id', definitionId);
        }

        // Serialize back to string
        const serializer = new XMLSerializer();
        return serializer.serializeToString(xmlDoc);
      }
    } catch (error) {
      console.warn('Failed to update BPMN XML with process info:', error);
    }

    // Return original XML if parsing fails
    return xmlContent;
  }

  /**
   * Update an existing BPMN process
   */
  updateBpmnProcess(
    id: string,
    processName: string,
    processDefineId: string,
    xmlContent: string,
  ): Observable<boolean> {
    const processId = Number.parseInt(id, 10);

    const processDefineDTO: ProcessDefineDTO = {
      id: processId,
      processName: processName,
      processDefineId: processDefineId,
      xmlString: xmlContent,
      processFileName: `${processDefineId}.bpmn`,
    };

    return this.processDefineController.update4(processDefineDTO).pipe(
      map((response) => {
        return true;
      }),
      catchError((error) => {
        console.error('Error updating process:', error);
        return of(false);
      }),
    );
  }

  /**
   * Create a new BPMN process
   */
  createBpmnProcess(
    process: Omit<BpmnItem, 'id' | 'createdAt' | 'updatedAt'>,
  ): Observable<BpmnItem> {
    const newProcess: BpmnItem = {
      ...process,
      id: (this.mockData.length + 1).toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.mockData.unshift(newProcess);
    return of(newProcess).pipe(delay(1000));
  }

  /**
   * Download BPMN file as XML
   */
  downloadBpmnFile(process: BpmnItem): Observable<boolean> {
    return this.getBpmnXmlContent(process.id).pipe(
      map((xmlContent) => {
        if (xmlContent) {
          // Create blob with XML content
          const blob = new Blob([xmlContent], { type: 'application/xml' });

          // Create download link
          const url = globalThis.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = process.fileName || `${process.definitionId}.bpmn`;

          // Trigger download
          document.body.appendChild(link);
          link.click();
          link.remove();

          // Clean up
          globalThis.URL.revokeObjectURL(url);

          return true;
        }
        return false;
      }),
      catchError((error) => {
        console.error('Error downloading BPMN file:', error);
        return of(false);
      }),
    );
  }

  /**
   * Filter and paginate mock data
   */
  private filterAndPaginateData(params: BpmnSearchParams): BpmnResponse {
    let filteredData = [...this.mockData];

    // Apply search filter
    if (params.processName) {
      filteredData = filteredData.filter(
        (process) =>
          process.name
            .toLowerCase()
            .includes(params.processName!.toLowerCase()) ||
          process.definitionId
            .toLowerCase()
            .includes(params.processName!.toLowerCase()),
      );
    }

    // Calculate pagination
    const totalRecords = filteredData.length;
    const startIndex = (params.page - 1) * params.size;
    const endIndex = startIndex + params.size;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    return {
      data: paginatedData,
      totalRecords,
      page: params.page,
      size: params.size,
    };
  }
}
